import * as React from 'react'

import {
  Box,
  Paper,
  Dialog,
  DialogContent,
  DialogTitle,
  Icon,
  DialogActions,
  IconButton,
  Divider,
  Typography,
} from '@design/pt-ui'

import { useDirtySanchez } from './DirtySanchez'
import ConfirmationDialog from './ConfirmationDialog'
import CreativesTransition from './CreativesTransition'

import { Formatset } from '../interfaces/entities/formatset'
import { Creative } from '../interfaces/entities/creative'
import { translateWithUserLocale } from '../utils/i18n'

type CreativeWithFormatset = {
  creative: Creative
  formatset: Formatset
}

interface ICreativeSelectionProps {
  creatives: CreativeWithFormatset[]
  dialogOpened: boolean
  onCloseDialog: () => void
  onActionComplete: (response: {
    creatives: Creative[]
    warnings: string[]
  }) => any
}

interface IState {
  dialogShown: boolean
}

function CreativeSelection(props: ICreativeSelectionProps) {
  const [state, setState] = React.useState<IState>({
    dialogShown: false,
  })

  const { isAnyDirty, clean } = useDirtySanchez()

  const closeDialog = () => {
    if (isAnyDirty) {
      setState({
        dialogShown: true,
      })
    } else {
      props.onCloseDialog()
    }
  }

  const formatsetIsValidFor = props.creatives
    .filter((c) => !c.formatset.invalid)
    .map((c) => c.creative.id)

  const transitionFilter = (creative: Creative) =>
    creative.status.name === 'specification_finalized' &&
    formatsetIsValidFor.includes(creative.id)

  const preselectedFilter = (creative: Creative) => {
    return creative.status.name === 'specification_finalized'
  }

  const onConfirm = () => {
    if (state.dialogShown && props.onCloseDialog) {
      props.onCloseDialog()
      setState({
        dialogShown: false,
      })
      clean('creatives_transition')
      clean('sent_to_agency')
    }
  }

  const onCloseConfirmDialog = () => {
    setState({
      dialogShown: false,
    })
    clean('creatives_transition')
    clean('sent_to_agency')
  }

  const { creatives } = props

  return (
    <>
      <Dialog
        disableRestoreFocus={true}
        open={props.dialogOpened}
        maxWidth="lg">
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
          <DialogTitle sx={{ margin: '24px 0' }}>
            {translateWithUserLocale('creative_selection_modal.title')}
          </DialogTitle>
          <DialogActions>
            <IconButton onClick={closeDialog} sx={{ color: 'primary.main' }}>
              <Icon>close</Icon>
            </IconButton>
          </DialogActions>
        </Box>
        <Typography
          variant="body1"
          sx={{
            paddingLeft: 4,
            paddingRight: 4,
            fontStyle: 'italic',
            fontWeight: 500,
            fontSize: '0.9rem',
          }}>
          {translateWithUserLocale('creative_selection_modal.subtitle')}
        </Typography>
        <Divider />
        <DialogContent
          sx={{
            backgroundColor: 'custom.gray.background',
            paddingTop: '25px',
          }}>
          <Paper>
            <Paper sx={{ padding: '15px', position: 'relative' }}>
              <CreativesTransition
                creatives={creatives.map((c) => c.creative)}
                action={{
                  name: 'send',
                  title: translateWithUserLocale(
                    'creative_selection_modal.send_to_agency',
                  ),
                  description: translateWithUserLocale(
                    'creative_selection_modal.send_to_agency',
                  ),
                }}
                onActionComplete={props.onActionComplete}
                selectionFilter={(creative: any) => transitionFilter(creative)}
                preselectedFilter={preselectedFilter}
                creativeGroup={(creative: any) => {
                  const result = creatives.find((cf) => {
                    return cf.creative.id === creative.id
                  })

                  if (!result) {
                    return ''
                  }

                  return result.formatset.name
                }}
              />
            </Paper>
          </Paper>
        </DialogContent>
      </Dialog>
      <ConfirmationDialog
        open={state.dialogShown}
        onClose={onCloseConfirmDialog}
        onConfirm={onConfirm}
        title={translateWithUserLocale('warning.unsaved_changes')}
      />
    </>
  )
}

export default CreativeSelection
